/* eslint-disable react-hooks/exhaustive-deps */
import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import moment from 'moment';
// components
import { Box, Button, Typography } from '@mui/material';
import OverviewTab from 'src/sections/@dashboard/lead/OverviewTab';
import AttendedChecklistV2, { useAttendedChecklistVisibility } from 'src/sections/@dashboard/leadDetail/AttendedChecklistV2';
import NotesTab from 'src/sections/@dashboard/lead/NotesTab';
import CallLogs from 'src/sections/@dashboard/leadDetail/CallLogs';
import IosEmailComposerBody from 'src/sections/tasks/composers/IosEmailComposerBody';
import FollowUpWhatsappChat from 'src/components/whatsappComponents/FollowUpWhatsappChat';
import { callApi } from 'src/utils/apiCall';
import useGuidedChecklist from 'src/sections/@dashboard/leadDetail/AttendedGuidedChecklist/hooks/useGuidedChecklist';
import { AttendedAppointment } from '../Attended';
import { clearLead, getLeadNotes, getLeadReminderData } from '../../redux/slices/lead';
import { setCurrentTreatmentDataGlobal } from '../../redux/slices/multiTreatment';
import { useSelector, dispatch } from '../../redux/store';
import {
    selectLeadFlowReminder,
    selectLeadNotes,
    selectLeadNotesArr,
    selectFollowLeadNotFound,
} from '../../redux/selectors/leadSelectors';
import AttendedFullscreenWrapper from './AttendedFullscreenWrapper';

export default function OpenAttendedPopup({ isOverview, open, onClose, type = 'lead', user, inputRef, headerButtons, editDetailHandler, OpenAssignTask, handleCloseAndReload, handleReloadLeads, handleNoAttended, debugSource = 'unknown' }) {
    const [sidebarTab, setSidebarTab] = useState('overview');
    // sidebarPulse is incremented on each programmatic open so the glow ring replays.
    const [sidebarPulse, setSidebarPulse] = useState(0);
    // ETM: per-practice email template to preload in the sidebar composer, set
    // when the checklist email action resolves a mapping (null = blank composer).
    const [sidebarEmailTemplateId, setSidebarEmailTemplateId] = useState(null);
    const [treatmentData, setTreatmentData] = useState(null);
    const [notesLength, setNotesLength] = useState([]);
    const { practiceId } = useSelector((state) => state.practiceTreatmentList);
    const leadFlowReminder = useSelector(selectLeadFlowReminder);
    const leadNotes = useSelector(selectLeadNotes);
    const leadNotesArr = useSelector(selectLeadNotesArr);
    const followLead = useSelector((state) => state.lead.followLead);
    const followLeadNotFound = useSelector(selectFollowLeadNotFound);

    const currentLeadId = user?.lead_id || user?.id;
    const sidebarLeadDetail = followLead || (user ? { ...user, id: currentLeadId } : null);
    const leadDetailForChecklist = (followLead && Number(followLead.id) === Number(currentLeadId))
        ? followLead
        : { id: currentLeadId, lead_treatments: user?.lead_treatments || [] };

    const attendedVisibility = useAttendedChecklistVisibility({
        practiceId,
        leadId: currentLeadId,
        leadDetail: leadDetailForChecklist,
    });

    // Change 5: checklist progress for the header badge (shared store — no extra fetch).
    const { groups: checklistGroups, allDoneCount } = useGuidedChecklist({
        practiceId,
        leadId: currentLeadId,
        leadDetail: leadDetailForChecklist,
    });
    const checklistDoneRaw   = (checklistGroups?.done?.length ?? 0);
    const checklistActiveRaw = (checklistGroups?.today?.length ?? 0) + (checklistGroups?.later?.length ?? 0);
    const checklistTotal = allDoneCount > 0 ? allDoneCount : (checklistDoneRaw + checklistActiveRaw);
    const checklistDone  = allDoneCount > 0 ? allDoneCount : checklistDoneRaw;

    useEffect(() => {
        const currentLeadId = user?.lead_id || user?.id;
        if (!practiceId || !currentLeadId) return;
        setNotesLength([]);
        dispatch(getLeadNotes(currentLeadId, practiceId));
        dispatch(getLeadReminderData(practiceId, 'LeadDetail', 10, 0, 'leadNotes', currentLeadId, '', '', '', 'counter', '', false, true));
    }, [user?.id, user?.lead_id, practiceId]);

    useEffect(() => {
        const fetchData = async () => {
            const currentLeadId = user?.lead_id || user?.id;
            if (!practiceId || !currentLeadId) return;
            try {
                const resp = await callApi(`/api/v1/practices/${practiceId}/leads/${currentLeadId}/treatments`, 'GET');
                setTreatmentData(resp?.data?.results ?? []);
            } catch (error) {
                console.error('Error fetching treatment data:', error);
            }
        };
        fetchData();
    }, [practiceId, user?.id, user?.lead_id]);

    useEffect(() => {
        const combineNotes = [];
        if (leadNotes?.length > 0) {
            combineNotes.push(...leadNotes.filter((el) => el.additional_notes !== ''));
        }
        if (leadNotesArr?.results?.length > 0) {
            combineNotes.push(...leadNotesArr.results.filter((el) => el.note !== ''));
        }
        if (combineNotes.length) {
            setNotesLength(
                combineNotes.sort(
                    (a, b) =>
                        new Date(moment(a.updated_at, 'DD-MM-YYYY HH:mm:ss')) -
                        new Date(moment(b.updated_at, 'DD-MM-YYYY HH:mm:ss'))
                )
            );
        }
    }, [user, leadNotes, leadNotesArr]);

    // Reset pulse counter when the popup closes
    useEffect(() => {
        if (!open) setSidebarPulse(0);
    }, [open]);

    const handleTabChange = (event, newValue) => setSidebarTab(newValue);

    // Programmatic tab open — bumps pulse so the glow ring replays
    const openSidebarTab = (v, opts) => {
        setSidebarTab(v);
        if (v === 'email') setSidebarEmailTemplateId(opts?.templateId ?? null);
        setSidebarPulse((n) => n + 1);
    };

    const handleClose = async (action) => {
        if (action === 'tco') {
            return;
        }
        dispatch(clearLead());
        dispatch(setCurrentTreatmentDataGlobal(null));
        onClose();
    };

    const SIDEBAR_TABS = [
        {
            icon: 'grommet-icons:overview',
            label: 'Overview',
            value: 'overview',
            component: (
                <OverviewTab
                    treatmentTabData={treatmentData || []}
                    leadId={user?.lead_id || user?.id}
                    setExpandLayout={() => {}}
                    handleChange={handleTabChange}
                    expandLayout={false}
                    leadReminder={leadFlowReminder}
                    user={user}
                    LeadNotes={notesLength}
                    showDetailModes
                />
            ),
            width: 24,
            height: 24,
        },
        ...(attendedVisibility.visible ? [{
            icon: 'mdi:checkbox-marked-outline',
            label: 'Attended Checklist',
            value: 'checklist',
            component: (
                <AttendedChecklistV2
                    practiceId={practiceId}
                    leadId={currentLeadId}
                    leadDetail={leadDetailForChecklist}
                />
            ),
            width: 20,
            height: 20,
        }] : []),
        {
            icon: 'solar:chat-round-dots-bold',
            label: 'Notes',
            value: 'notes',
            component: (
                <NotesTab
                    LeadNotes={notesLength}
                    isForTab
                    userDetails={user}
                    handleChange={handleTabChange}
                />
            ),
            width: 20,
            height: 20,
        },
        {
            icon: 'solar:phone-calling-rounded-bold',
            label: 'Calls',
            value: 'calls',
            component: (
                <CallLogs leadDetail={sidebarLeadDetail} />
            ),
            width: 20,
            height: 20,
        },
        {
            icon: 'mdi:email-outline',
            label: 'Email',
            value: 'email',
            component: (
                <IosEmailComposerBody
                    leadId={followLead?.id || user?.lead_id || user?.id}
                    practiceId={practiceId}
                    leadDetail={followLead || user}
                    templateId={sidebarEmailTemplateId}
                    compact
                />
            ),
            width: 20,
            height: 20,
        },
        {
            icon: 'ic:baseline-whatsapp',
            label: 'WhatsApp',
            value: 'whatsapp',
            component: (
                <FollowUpWhatsappChat
                    user={followLead || user}
                    followLead={followLead}
                />
            ),
            width: 20,
            height: 20,
        },
    ];

    // Appointment filter: attended popup uses BOOKED or ATTENDED appointments
    const appointmentFilter = (appts) =>
        appts.filter((el) => el.status === 'BOOKED' || el.status === 'ATTENDED');

    return (
        <AttendedFullscreenWrapper
            open={open}
            onClose={handleClose}
            type={type}
            headerButtons={headerButtons}
            headerTitle={
                <>
                    Attended appointment for{' '}
                    <span style={{ textTransform: 'capitalize' }}>{user.first_name}</span>
                </>
            }
            followLead={followLead}
            popupName="Attended"
            pinLeadId={currentLeadId || null}
            pinLeadPracticeId={practiceId || null}
            headerCountStatus="CONSULTATION_BOOKED"
            appointmentFilter={appointmentFilter}
            sidebarTabs={SIDEBAR_TABS}
            sidebarTab={sidebarTab}
            onTabChange={handleTabChange}
            sidebarPulseKey={sidebarPulse}
            pulseCssName="attendedPulse"
            checklistDone={checklistDone}
            checklistTotal={checklistTotal}
        >
            {followLeadNotFound && !followLead ? (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        gap: 2,
                        px: 3,
                    }}
                >
                    <Typography variant="h5" color="text.secondary" textAlign="center">
                        This patient is no longer available
                    </Typography>
                    <Typography variant="body2" color="text.disabled" textAlign="center">
                        The patient may have been archived or marked as spam.
                    </Typography>
                    <Button variant="outlined" onClick={() => handleClose('close')} sx={{ mt: 1 }}>
                        Close
                    </Button>
                </Box>
            ) : (
            <AttendedAppointment
                // Remount per lead so all useState/useRef inside (gateDismissed,
                // activeTab, attendanceRecorded, etc.) reset when a different lead
                // opens — this Dialog is persistent and never unmounts between leads.
                // Key is stable for a given lead (only changes when the lead changes),
                // so there is no remount loop.
                key={user?.lead_id || user?.id}
                isOverview={isOverview}
                isFinishButtonAvailable
                handleCloseAndReload={handleCloseAndReload}
                handleClose={handleClose}
                id={user.lead_id}
                user={user}
                type='Starting treatment'
                inputRef={inputRef}
                OpenAssignTask={OpenAssignTask}
                editDetailHandler={editDetailHandler}
                handleReloadLeads={handleReloadLeads}
                handleNoAttended={handleNoAttended}
                onOpenSidebarTab={openSidebarTab}
                debugSource={debugSource}
            />
            )}
        </AttendedFullscreenWrapper>
    );
}

OpenAttendedPopup.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
    type: PropTypes.string,
    user: PropTypes.object,
    inputRef: PropTypes.any,
    headerButtons: PropTypes.bool,
    editDetailHandler: PropTypes.any,
    OpenAssignTask: PropTypes.any,
    handleCloseAndReload: PropTypes.func,
    handleReloadLeads: PropTypes.func,
    isOverview: PropTypes.bool,
    handleNoAttended: PropTypes.func,
    /** Where this popup was opened from — surfaced in the superadmin debug overlay. */
    debugSource: PropTypes.string,
};
